import type { InvoicePrintOptions } from "./invoicePrintDocument";
import type { Company, InvoiceRecord } from "../types";

export type InvoiceLineItemForm = {
  id: string;
  description: string;
  quantity: string;
  unit_price: string;
  hsn_sac: string;
  tax_rate: string;
};

/** Form fields that map 1:1 to the tax invoice PDF (seller/bank come from company). */
export type InvoiceFormProfile = {
  sellerName: string;
  sellerAddress: string;
  sellerGstin: string;
  sellerState: string;
  sellerStateCode: string;
  buyerName: string;
  buyerAddress: string;
  buyerState: string;
  buyerStateCode: string;
  consigneeSameAsBuyer: boolean;
  consigneeName: string;
  consigneeAddress: string;
  consigneeState: string;
  consigneeStateCode: string;
  /** Default HSN/SAC for line items + tax summary */
  category: string;
  paymentTerms: string;
  /** ISO date (yyyy-mm-dd), or "" if unset. */
  dueDate: string;
  bankAccountHolder: string;
  bankAccountNumber: string;
  bankIfscCode: string;
  bankBranch: string;
  lineItems: InvoiceLineItemForm[];
};

const PRINT_KEY_PREFIX = "zoniq-invoice-print-";

export function createEmptyLineItem(): InvoiceLineItemForm {
  return {
    id: crypto.randomUUID(),
    description: "",
    quantity: "",
    unit_price: "",
    hsn_sac: "",
    tax_rate: "",
  };
}

/** Blank profile for the generate-invoice form (Clear). */
export function createClearedInvoiceProfile(): InvoiceFormProfile {
  return createEmptyInvoiceProfile();
}

export function createEmptyInvoiceProfile(): InvoiceFormProfile {
  return {
    sellerName: "",
    sellerAddress: "",
    sellerGstin: "",
    sellerState: "",
    sellerStateCode: "",
    buyerName: "",
    buyerAddress: "",
    buyerState: "",
    buyerStateCode: "",
    consigneeSameAsBuyer: true,
    consigneeName: "",
    consigneeAddress: "",
    consigneeState: "",
    consigneeStateCode: "",
    category: "",
    paymentTerms: "",
    dueDate: "",
    bankAccountHolder: "",
    bankAccountNumber: "",
    bankIfscCode: "",
    bankBranch: "",
    lineItems: [createEmptyLineItem()],
  };
}

function readStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  return window.localStorage;
}

export function saveInvoicePrintProfile(invoiceId: string, profile: InvoiceFormProfile): void {
  readStorage()?.setItem(`${PRINT_KEY_PREFIX}${invoiceId}`, JSON.stringify(profile));
}

export function loadInvoicePrintProfile(invoiceId: string): InvoiceFormProfile | null {
  const raw = readStorage()?.getItem(`${PRINT_KEY_PREFIX}${invoiceId}`);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<InvoiceFormProfile>;
    return {
      ...createEmptyInvoiceProfile(),
      ...parsed,
      lineItems:
        Array.isArray(parsed.lineItems) && parsed.lineItems.length > 0
          ? parsed.lineItems.map((item) => ({
              ...createEmptyLineItem(),
              ...item,
              id: item.id ?? crypto.randomUUID(),
            }))
          : [createEmptyLineItem()],
    };
  } catch {
    return null;
  }
}

/** Maps the business-owner Company profile to invoice defaults — the single source of seller/bank identity. */
export function profileFromCompany(company: Company, fallback?: InvoiceFormProfile): InvoiceFormProfile {
  const base = fallback ?? createEmptyInvoiceProfile();
  return {
    ...base,
    sellerName: company.name ?? base.sellerName,
    sellerAddress: company.invoice_address ?? base.sellerAddress,
    sellerGstin: company.invoice_gstin ?? base.sellerGstin,
    sellerState: company.invoice_state_name ?? base.sellerState,
    sellerStateCode: company.invoice_state_code ?? base.sellerStateCode,
    bankAccountHolder: company.bank_account_holder ?? base.bankAccountHolder,
    bankAccountNumber: company.bank_account_number ?? base.bankAccountNumber,
    bankIfscCode: company.bank_ifsc_code ?? base.bankIfscCode,
    bankBranch: company.bank_branch ?? base.bankBranch,
  };
}

export function profileFromInvoiceRecord(
  detail: InvoiceRecord,
  fallback?: InvoiceFormProfile | null
): InvoiceFormProfile {
  const base = fallback ?? createEmptyInvoiceProfile();
  const items = detail.line_items ?? [];
  const formData = (detail.form_data ?? {}) as Partial<InvoiceFormProfile>;
  return {
    ...base,
    ...formData,
    buyerName: detail.vendor_name?.trim() ?? formData.buyerName ?? base.buyerName,
    buyerAddress: detail.vendor_address?.trim() ?? formData.buyerAddress ?? base.buyerAddress,
    category: detail.category?.trim() ?? formData.category ?? base.category,
    lineItems:
      items.length > 0
        ? items.map((item, index) => ({
            id: formData.lineItems?.[index]?.id ?? base.lineItems[index]?.id ?? crypto.randomUUID(),
            description: item.description,
            quantity: String(item.quantity),
            unit_price: String(item.unit_price),
            hsn_sac: formData.lineItems?.[index]?.hsn_sac ?? base.lineItems[index]?.hsn_sac ?? base.category,
            tax_rate: formData.lineItems?.[index]?.tax_rate ?? base.lineItems[index]?.tax_rate ?? "",
          }))
        : formData.lineItems && formData.lineItems.length > 0
          ? formData.lineItems.map((item) => ({ ...createEmptyLineItem(), ...item, id: item.id ?? crypto.randomUUID() }))
          : base.lineItems,
  };
}

/** Maps form data to PDF options. Seller/bank fields on `profile` should already be sourced from the company profile via `profileFromCompany`, not the platform. */
export function profileToPrintOptions(
  profile: InvoiceFormProfile,
  detail: InvoiceRecord
): InvoicePrintOptions {
  const consignee = profile.consigneeSameAsBuyer
    ? {
        name: profile.buyerName,
        address: profile.buyerAddress,
        state: profile.buyerState,
        stateCode: profile.buyerStateCode,
      }
    : {
        name: profile.consigneeName,
        address: profile.consigneeAddress,
        state: profile.consigneeState,
        stateCode: profile.consigneeStateCode,
      };

  return {
    sellerName: profile.sellerName.trim(),
    sellerAddress: profile.sellerAddress,
    sellerGstin: profile.sellerGstin,
    sellerState: profile.sellerState,
    sellerStateCode: profile.sellerStateCode,
    buyerName: profile.buyerName.trim() || detail.vendor_name,
    buyerAddress: profile.buyerAddress.trim() || detail.vendor_address,
    buyerState: profile.buyerState,
    buyerStateCode: profile.buyerStateCode,
    consigneeName: consignee.name,
    consigneeAddress: consignee.address,
    consigneeState: consignee.state,
    consigneeStateCode: consignee.stateCode,
    paymentTerms: profile.paymentTerms,
    bankAccountHolder: profile.bankAccountHolder,
    bankAccountNumber: profile.bankAccountNumber,
    bankIfscCode: profile.bankIfscCode,
    bankBranch: profile.bankBranch,
    categoryHsn: profile.category,
    lineItemMeta: profile.lineItems.map((item) => ({
      hsn_sac: item.hsn_sac,
      tax_rate: item.tax_rate,
    })),
  };
}
